import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnInit,
    PLATFORM_ID,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

// ** rxjs
import { Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { ProductsService } from '@app/core/services/products.service';
import { SuccessService } from '@app/core/services/success.service';
import { TranslateService } from '@ngx-translate/core';

//  ** env
import { environment } from '@env/environment';

import { SwiperComponent } from 'swiper/angular';

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
})
export class ProductComponent implements OnInit {
    @ViewChildren(SwiperComponent) swipers: QueryList<SwiperComponent>;

    isBrowser: boolean;

    // ** 該單一產品全部資料
    fullPiece: any;
    // ** 產品 key （用於翻譯路徑）
    productKey: string = '';
    // ** 全部產品清單
    productsSheet;
    // ** 全部解決方案清單
    successSheet;

    // ** swiper current 頁數 reference
    swiperPageRef = 1;
    // ** swiper 顯示的數量
    perPage = 3;

    // ** 相關 topics arr
    relatedPieces = [];
    // ** 相關 tags
    relation = [];

    showSwiper = false;

    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
        if (this.isBrowser) this.onWindowResize();
    }

    // - 依據 window size 調整 swiper 顯示 slide 數量
    onWindowResize() {
        const lg = window.innerWidth > 1024;
        const md = window.innerWidth > 440 && window.innerWidth <= 1024;
        const sm = window.innerWidth <= 440;
        this.perPage = lg ? 3 : md ? 2 : sm ? 1 : 0;
        this.updateScroll();
    }

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId,
        private route: ActivatedRoute,
        private title: Title,
        private router: Router,
        private productsService: ProductsService,
        private layoutService: LayoutService,
        private cdr: ChangeDetectorRef,
        private successService: SuccessService,
        private translateService: TranslateService,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        if (this.isBrowser) this.onWindowResize();
    }

    ngOnInit(): void {
        this.initPage();
    }

    initPage() {
        this.route.params
            .pipe(
                tap(() => this.layoutService.setPageLoading(true)),
                tap(params => {
                    const canonical = this.document.querySelector(
                        'link[rel="canonical"]',
                    ) as HTMLAnchorElement | null;
                    console.log(params['title']);
                    if (canonical)
                        canonical.href = `https://www.kunyoutech.com.tw/products/${params['title']}`;
                }),
                switchMap(params => {
                    this.productKey = this.getProductKeyFromTitle(
                        params['title'],
                    );
                    return this.getFullPiece$(`${params['title']}.json`);
                }),
                tap(res => (this.fullPiece = res)),
                tap(() =>
                    this.setTitle(
                        this.translateService.instant(
                            `products.products.${this.productKey}.name`,
                        ),
                    ),
                ),
                tap(() => (this.relation = this.fullPiece?.Tags)),
                tap(() => this.getProductsSheet()),
                tap(() => this.getSuccessSheet()),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 設定 title for seo
    setTitle(title) {
        if (title)
            this.title.setTitle(`${environment.projectTitleCH} - ${title}`);
    }

    // - 更新頁數
    updatePageIdx() {
        setTimeout(() => {
            this.swiperPageRef = this.swipers?.last?.swiperRef
                ? this.swipers?.last?.swiperRef.activeIndex + 1
                : 1;
            this.cdr.detectChanges();
        });
    }

    // - manual update swiper scroll for ng lifecycle hotfix
    updateScroll() {
        setTimeout(() => {
            this.swipers?.last?.swiperRef?.scrollbar.updateSize();
            this.swipers?.last?.swiperRef?.scrollbar.setTranslate();
        });
    }

    // - 取得產品全 (sheet)
    getProductsSheet() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getProductsSheet$()),
                tap(res => (this.productsSheet = [...res])),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 取得成功案例清單
    getSuccessSheet() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getSuccessSheet$()),
                tap(res => (this.successSheet = [...res])),
                tap(() => this.findRelated()),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 找到相關成功案例主題
    findRelated() {
        if (!this.relation.length) return this.preventNoneSuccess();
        this.successSheet.some((piece, idx) => {
            if (this.relatedPieces.length === 9) return true;
            // ** 找相關 tag
            const isRelated = piece.MetaTags.some(x =>
                this.relation.some(
                    y =>
                        new RegExp(y, 'i').test(x) ||
                        new RegExp(x, 'i').test(y),
                ),
            );
            if (isRelated) this.relatedPieces = [...this.relatedPieces, piece];
        });
        if (!this.relatedPieces.length) this.preventNoneSuccess();
        this.initSwiper();
    }

    // - 避免成功案例全部都沒有
    preventNoneSuccess() {
        this.relatedPieces = [...this.successSheet.slice(0, 9)];
    }

    // - 前往成功案例內頁
    goToSuccess(NameEN) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() =>
                    setTimeout(() =>
                        this.router.navigateByUrl(`success/${NameEN}`),
                    ),
                ),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 初始化 swiper
    initSwiper() {
        setTimeout(() => (this.showSwiper = true));
        setTimeout(() =>
            this.swipers?.last?.swiperRef?.on('slideChange', e =>
                this.updatePageIdx(),
            ),
        );
    }

	/**
	 * 從路由標題取得產品 key
	 * 例如：'ai-license-plate-recognition-system' -> 'aiLicensePlateRecognitionSystem'
     * @param title 路由參數
     * @returns 翻譯 key
     */
    getProductKeyFromTitle(title: string): string {
        return title
            .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())
            .replace(/^[a-z]/, match => match.toLowerCase());
    }

	/**
	 * 英文名稱轉換為對應的 key
	 * ex：'mobile-public-service-duty-app' -> 'mobilePublicServiceDutyApp'
	 */
    getSuccessKeyFromNameEN(nameEN: string): string {
        if (!nameEN) return '';

        return nameEN
            .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())
            .replace(/^[a-z]/, letter => letter.toLowerCase());
    }

    /**
     * 翻譯後的類型部分（可指定取｜之前或之後的部分）
     * @param nameEN 名稱的英文版本
     * @param part 'before' | 'after' | 'all' - 指定要取得的部分
     * @returns 根據part參數返回對應的部分
     */
    getTranslatedTypeParts(
        nameEN: string,
        part: 'before' | 'after' | 'all' = 'all',
    ): string | string[] {
        const typeKey = `solutions.explore.successCases.${this.getSuccessKeyFromNameEN(
            nameEN,
        )}.type`;
        const translatedType = this.translateService.instant(typeKey);
        const parts = translatedType.split(' ｜ ');

        switch (part) {
            case 'before':
                return parts[0] || '';
            case 'after':
                return parts.slice(1);
            case 'all':
            default:
                return parts;
        }
    }
        
    // ? API
    getProductsSheet$(): Observable<any> {
        return this.productsService.getProductsSheet().pipe();
    }
    getFullPiece$(file: string): Observable<any> {
        return this.productsService.getFullPiece(file).pipe();
    }

    getSuccessSheet$(): Observable<any> {
        return this.successService.getSuccessSheet().pipe();
    }
}
